import { NoteComposer } from '@/components/notes/note-composer';
import { NoteItem } from '@/components/notes/note-item';
import { authorName, noteErrorMessage } from '@/lib/note-format';
import type { NoteThreadActions, NoteViewer } from '@/lib/notes-queries';
import { cn } from '@/lib/utils';
import type { NoteThread as NoteThreadData } from '@mss_creator/shared';
import { Button, Icon } from '@pms-digital/design-system';
import { useState } from 'react';

export interface NoteThreadProps {
  thread: NoteThreadData;
  viewer: NoteViewer;
  /** Named in the level picker and in « Visible par ». */
  companyName: string;
  actions: NoteThreadActions;
  /** Shows the label of the target above the discussion, for a list that spans several targets. */
  showTarget?: boolean;
  /** A resolved discussion opens unfolded (otherwise it is one folded line). */
  defaultExpanded?: boolean;
  /** The clock of the relative dates; tests pin it. */
  now?: Date;
  className?: string;
}

const _SMALL_BUTTON = 'h-7 gap-1 px-2 text-caption';

/**
 * A discussion (planche 09): the root note, its replies set in under a left
 * border, « Répondre… » opening a reply field, and « Résoudre » / « Rouvrir ».
 * A resolved discussion is folded to one line until opened; a note whose
 * dimension is gone carries its « Portait sur la dimension « X », supprimée »
 * at the top.
 */
export function NoteThread({
  thread,
  viewer,
  companyName,
  actions,
  showTarget = false,
  defaultExpanded = false,
  now,
  className,
}: NoteThreadProps) {
  const resolved = thread.resolvedAt !== null;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [replying, setReplying] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggleResolved() {
    setToggling(true);
    setError(null);
    try {
      await (resolved ? actions.reopen(thread.id) : actions.resolve(thread.id));
    } catch (failure) {
      setError(
        noteErrorMessage(
          failure,
          resolved
            ? "La discussion n'a pas pu être rouverte. Réessayez."
            : "La discussion n'a pas pu être résolue. Réessayez.",
        ),
      );
    } finally {
      setToggling(false);
    }
  }

  const toggleButton = (
    <Button type="button" variant="ghost" disabled={toggling} onClick={() => void toggleResolved()}>
      {resolved ? <Icon name="undo" size={14} /> : <Icon name="check" size={14} />}
      {resolved ? (toggling ? 'Réouverture…' : 'Rouvrir') : toggling ? 'Résolution…' : 'Résoudre'}
    </Button>
  );

  if (resolved && !expanded) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-caption',
          className,
        )}
      >
        <button
          type="button"
          aria-expanded={false}
          onClick={() => setExpanded(true)}
          className="flex min-w-0 flex-1 items-center gap-1 rounded-sm text-left text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          <Icon name="chevron_right" size={14} />
          <span className="truncate">
            <b className="font-semibold">{authorName(thread.author)}</b> {thread.body}
          </span>
        </button>
        {toggleButton}
        {error && (
          <p role="alert" className="font-semibold text-rouge-deep">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border border-gray-200 bg-white px-3 py-2', className)}>
      {thread.orphanLabel && (
        <p className="mb-1 flex items-center gap-1 text-gray-600 text-micro">
          <Icon name="info" size={14} />
          {thread.orphanLabel}
        </p>
      )}
      {showTarget && <p className="mb-1 text-bleu-nuit text-micro">{thread.targetLabel}</p>}
      <NoteItem
        note={thread}
        variant="root"
        viewer={viewer}
        companyName={companyName}
        replyCount={thread.replies.length}
        onUpdate={(input) => actions.update(thread.id, input)}
        onDelete={() => actions.remove(thread.id)}
        {...(now ? { now } : {})}
      />
      {thread.replies.length > 0 && (
        <div className="mt-2 flex flex-col gap-2 border-gray-200 border-l-2 pl-2">
          {thread.replies.map((reply) => (
            <NoteItem
              key={reply.id}
              note={reply}
              variant="reply"
              viewer={viewer}
              companyName={companyName}
              onUpdate={(input) => actions.update(reply.id, input)}
              onDelete={() => actions.remove(reply.id)}
              {...(now ? { now } : {})}
            />
          ))}
        </div>
      )}
      {replying && !resolved && (
        <NoteComposer
          mode="reply"
          role={viewer.role}
          companyName={companyName}
          className="mt-2"
          focusKey={0}
          onSubmit={async ({ body }) => {
            await actions.reply(thread.id, body);
            setReplying(false);
          }}
          onCancel={() => setReplying(false)}
        />
      )}
      <div className="mt-2 flex items-center gap-2 text-caption">
        {!resolved && !replying && (
          <button
            type="button"
            onClick={() => setReplying(true)}
            className="rounded-sm text-gray-600 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
          >
            Répondre…
          </button>
        )}
        {resolved && thread.resolvedBy && (
          <span className="text-gray-600">Résolue par {authorName(thread.resolvedBy)}</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {resolved && (
            <Button type="button" variant="ghost" onClick={() => setExpanded(false)}>
              Replier
            </Button>
          )}
          {toggleButton}
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-1 font-semibold text-caption text-rouge-deep">
          {error}
        </p>
      )}
    </div>
  );
}
